
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { toast } from "react-toastify";
import fetchSeatData from "../../../helpers/fetch-seat-data";

import {
  MpesaCheckout,
  MpesaHeading,
  MpesaHeaderIcon,
  MpesaEyebrow,
  MpesaSection,
  MpesaSectionHeading,
  MpesaStep,
  MpesaOption,
  MpesaLogo,
  MpesaOptionDetails,
  MpesaSelected,
  MpesaInstructions,
  MpesaInstructionItem,
  MpesaPhoneField,
  MpesaTotal,
  MpesaSecurityNote,
  MpesaFooterActions,
  MpesaStatusText,
  MpesaUnavailable,
  ButtonWrapper,
  CancelButton,
  PaymentButton,
  PaymentInput,
  PaymentUl,
  SinglePayment,
} from "./Payment.styles";

const MPESA_PAYMENT_METHOD_ID = 4;

// Convert different possible API seat-layout formats into an array.
const normalizeSeatLayout = (rawSeatLayout) => {
  let parsedLayout = rawSeatLayout;

  if (typeof parsedLayout === "string") {
    try {
      parsedLayout = JSON.parse(parsedLayout);
    } catch {
      throw new Error("The seat layout returned by the server is invalid.");
    }
  }

  if (Array.isArray(parsedLayout)) {
    return parsedLayout.flat(Infinity);
  }

  if (parsedLayout && typeof parsedLayout === "object") {
    // Some APIs wrap the seat array inside an object.
    const possibleSeatArray =
      parsedLayout.seats ||
      parsedLayout.seatLayout ||
      parsedLayout.seat_layout;

    if (Array.isArray(possibleSeatArray)) {
      return possibleSeatArray.flat(Infinity);
    }

    return Object.values(parsedLayout).flat(Infinity);
  }

  return [];
};

const Payment = ({
  handleSubmitPayment,
  setpaymentGateway,
  paymentGateway,
  setLoading,
  onBack,
  bookingTotal,
}) => {
  const history = useHistory();

  const { webSettingData, languageData } = useSelector(
    (state) => state.busLists
  );

  const [mpesaPhone, setMpesaPhone] = useState("");
  const [paymentMethodAvailable, setPaymentMethodAvailable] = useState(false);
  const [checkingPaymentMethod, setCheckingPaymentMethod] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkPaymentMethod = async () => {
      setCheckingPaymentMethod(true);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_MODULE_DOMAIN}/paymethods`
        );

        if (!response.ok) {
          throw new Error("Unable to check payment methods.");
        }

        const data = await response.json();

        const methods = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];

        const available = methods.some(
          (method) => Number(method.id) === MPESA_PAYMENT_METHOD_ID
        );

        if (!cancelled) {
          setPaymentMethodAvailable(available);

          if (available) {
            setpaymentGateway(String(MPESA_PAYMENT_METHOD_ID));
          } else {
            toast.error("M-PESA is currently unavailable.");
          }
        }
      } catch (error) {
        console.error("M-PESA availability check failed:", error);

        if (!cancelled) {
          setPaymentMethodAvailable(false);
          toast.error("Unable to check M-PESA availability.");
        }
      } finally {
        if (!cancelled) {
          setCheckingPaymentMethod(false);
        }
      }
    };

    checkPaymentMethod();

    return () => {
      cancelled = true;
    };
  }, [setpaymentGateway]);

  const handleCancel = () => {
    if (submitting) return;

    [
      "bookingInfo",
      "searchInfo",
      "returnFirstJourneyInfo",
      "journeyInfo",
      "discount",
      "regular",
      "return",
      "bookingInfoTax",
      "journeyInfoTax",
      "subtripId",
    ].forEach((key) => localStorage.removeItem(key));

    history.push("/");
  };

  const handleMpesaPayment = async () => {
    if (submitting) return;

    const normalizedInput = mpesaPhone.trim();

    if (!normalizedInput) {
      toast.error("Please enter your M-PESA phone number.");
      return;
    }

    if (!paymentMethodAvailable) {
      toast.error("M-PESA is currently unavailable.");
      return;
    }

    let bookingInfo;
    let searchInfo;

    try {
      bookingInfo = JSON.parse(
        localStorage.getItem("bookingInfo") || "{}"
      );

      searchInfo = JSON.parse(
        localStorage.getItem("searchInfo") || "{}"
      );
    } catch (error) {
      toast.error(
        "Your booking information could not be read. Please try again."
      );
      return;
    }

    const subtripId = bookingInfo?.subtripId;
    const seatnumbers = bookingInfo?.seatnumbers;

    if (!subtripId || !seatnumbers) {
      toast.error(
        "Your booking information is incomplete. Please try again."
      );
      return;
    }

    if (!searchInfo?.journeydate) {
      toast.error("Journey date is missing. Please try again.");
      return;
    }

    setSubmitting(true);
    setLoading(true);

    try {
      // Fetch the latest seat information before initiating payment.
      const result = await fetchSeatData(
        searchInfo.journeydate,
        subtripId
      );

      if (!result || !result.seatlayout) {
        throw new Error(
          "Unable to verify seat availability. Please try again."
        );
      }

      const selectedSeats = String(seatnumbers)
        .split(",")
        .map((seat) => seat.trim())
        .filter(Boolean);

      const seatLayout = normalizeSeatLayout(result.seatlayout);

      if (!seatLayout.length) {
        throw new Error(
          "No seat information was returned. Please try again."
        );
      }

      // Check whether any selected seats have already been reserved.
      const unavailableSeats = selectedSeats.filter((selectedSeat) => {
        const seat = seatLayout.find(
          (item) =>
            String(item?.seat_no) === String(selectedSeat)
        );

        if (!seat) {
          return false;
        }

        const reserved =
          seat.isReserved === true ||
          seat.isReserved === 1 ||
          seat.isReserved === "1";

        return reserved;
      });

      if (unavailableSeats.length > 0) {
        toast.error(
          `Seat ${unavailableSeats.join(", ")} is no longer available. Please select another seat.`
        );
        return;
      }

	// Initiate the booking and M-PESA STK Push.
	setpaymentGateway(String(MPESA_PAYMENT_METHOD_ID));

	const paymentResult = await handleSubmitPayment(normalizedInput);

	const checkoutRequestId = paymentResult?.checkout_request_id;
	const bookingId = paymentResult?.booking_id;

	if (checkoutRequestId && bookingId) {
	  // The STK request was accepted, but payment is not yet confirmed.
	  history.push(
		`/check-payment/${encodeURIComponent(bookingId)}?checkout_request_id=${encodeURIComponent(checkoutRequestId)}`
	  );
	} else if (paymentResult?.payment_status === "pending") {
	  toast.info(
		"Your payment request is being processed. Please do not submit another payment."
	  );
	} else {
	  throw new Error(
		paymentResult?.message ||
		  "M-PESA payment request could not be confirmed. Please contact support before trying again."
	  );
	}
	  
	  
	  
	  
    } catch (error) {
      console.error("M-PESA payment error:", error);

      toast.error(
        error?.message ||
          "Unable to initiate M-PESA payment. Please try again."
      );
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  const cancelLabel =
    languageData?.checkout_page_cancel_btn?.[
      webSettingData?.language
    ] || "Cancel";

  const formattedTotal = Number(bookingTotal || 0).toLocaleString(
    "en-KE",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );

  return (
    <MpesaCheckout>
      <MpesaHeading>
        <MpesaHeaderIcon>
          <i className="fa fa-lock" aria-hidden="true" />
        </MpesaHeaderIcon>

        <div>
          <MpesaEyebrow>SECURE CHECKOUT</MpesaEyebrow>
          <h3>Complete your payment</h3>
          <p>Your booking is almost complete. Choose how to pay.</p>
        </div>
      </MpesaHeading>

      <MpesaSection>
        <MpesaSectionHeading>
          <MpesaStep>01</MpesaStep>

          <div>
            <h4>Payment method</h4>
            <p>Select your payment option.</p>
          </div>
        </MpesaSectionHeading>

        <PaymentUl>
          <SinglePayment>
            <PaymentInput
              id="payment-mpesa"
              type="radio"
              name="payment"
              value={MPESA_PAYMENT_METHOD_ID}
              checked={
                String(paymentGateway) ===
                String(MPESA_PAYMENT_METHOD_ID)
              }
              onChange={() =>
                setpaymentGateway(String(MPESA_PAYMENT_METHOD_ID))
              }
              disabled={
                !paymentMethodAvailable || checkingPaymentMethod
              }
            />

            <MpesaOption htmlFor="payment-mpesa">
              <MpesaLogo
                src="/img/mpesa-logo.png"
                alt="M-PESA"
              />

              <MpesaOptionDetails>
                <strong>M-PESA</strong>
                <small>Safaricom STK Push</small>
              </MpesaOptionDetails>

              <MpesaSelected>
                {String(paymentGateway) ===
                String(MPESA_PAYMENT_METHOD_ID)
                  ? "Selected"
                  : "Select"}
              </MpesaSelected>
            </MpesaOption>
          </SinglePayment>
        </PaymentUl>

        {checkingPaymentMethod && (
          <MpesaStatusText>
            Checking M-PESA availability...
          </MpesaStatusText>
        )}
      </MpesaSection>

      <MpesaSection>
        <MpesaSectionHeading>
          <MpesaStep>02</MpesaStep>

          <div>
            <h4>Payment instructions</h4>
            <p>Three quick steps to complete your payment.</p>
          </div>
        </MpesaSectionHeading>

        <MpesaInstructions>
          <MpesaInstructionItem>
            <span>1</span>

            <div>
              <strong>Enter your phone number</strong>
              <small>Use your Safaricom M-PESA number.</small>
            </div>
          </MpesaInstructionItem>

          <MpesaInstructionItem>
            <span>2</span>

            <div>
              <strong>Receive payment request</strong>
              <small>An STK prompt will appear on your phone.</small>
            </div>
          </MpesaInstructionItem>

          <MpesaInstructionItem>
            <span>3</span>

            <div>
              <strong>Confirm with your PIN</strong>
              <small>Enter your M-PESA PIN to authorize payment.</small>
            </div>
          </MpesaInstructionItem>
        </MpesaInstructions>
      </MpesaSection>

      <MpesaSection>
        <MpesaSectionHeading>
          <MpesaStep>03</MpesaStep>

          <div>
            <h4>Payment phone number</h4>
            <p>Where should we send the payment request?</p>
          </div>
        </MpesaSectionHeading>

        <MpesaPhoneField>
          <label htmlFor="mpesa-phone">
            M-PESA mobile number <span>*</span>
          </label>

          <div className="phone-input-wrapper">
            <span className="phone-prefix">KE</span>

            <input
              id="mpesa-phone"
              type="tel"
              autoComplete="tel"
              inputMode="tel"
              placeholder="0712 345 678"
              value={mpesaPhone}
              onChange={(event) =>
                setMpesaPhone(event.target.value)
              }
              disabled={submitting}
            />

            <i className="fa fa-mobile" aria-hidden="true" />
          </div>

          <small>
            Ensure your phone is switched on and able to receive an
            M-PESA prompt.
          </small>
        </MpesaPhoneField>
      </MpesaSection>

      {bookingTotal !== undefined && bookingTotal !== null && (
        <MpesaTotal>
          <div>
            <span>Total amount payable</span>
            <small>Including applicable charges</small>
          </div>

          <strong>
            <small>KES</small> {formattedTotal}
          </strong>
        </MpesaTotal>
      )}

      <MpesaSecurityNote>
        <i className="fa fa-shield" aria-hidden="true" />
        <span>
          Your payment is processed securely through M-PESA.
        </span>
      </MpesaSecurityNote>

      <ButtonWrapper>
        <CancelButton
          type="button"
          onClick={onBack}
          disabled={submitting}
        >
          <i className="fa fa-arrow-left" aria-hidden="true" />
          Back
        </CancelButton>

        <PaymentButton
          type="button"
          onClick={handleMpesaPayment}
          disabled={
            checkingPaymentMethod ||
            !paymentMethodAvailable ||
            submitting
          }
          btnbghvcolor={webSettingData?.buttoncolorhover}
          btnbgcolor={webSettingData?.buttoncolor}
        >
          {checkingPaymentMethod
            ? "Loading M-PESA..."
            : submitting
              ? "Requesting STK Push..."
              : "Pay with M-PESA"}

          {!checkingPaymentMethod && !submitting && (
            <i className="fa fa-arrow-right" aria-hidden="true" />
          )}
        </PaymentButton>
      </ButtonWrapper>

      <MpesaFooterActions>
        <button
          type="button"
          onClick={handleCancel}
          disabled={submitting}
        >
          {cancelLabel}
        </button>
      </MpesaFooterActions>

      {!checkingPaymentMethod && !paymentMethodAvailable && (
        <MpesaUnavailable>
          M-PESA is currently unavailable. Please try again later.
        </MpesaUnavailable>
      )}
    </MpesaCheckout>
  );
};

export default Payment;
